import { type ProspectDto } from "@crowdcoursing/api/openapi";
import { Delete, Edit, MoreVert, PushPin } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from "@mui/material";
import Linkify from "linkify-react";
import { DateTime } from "luxon";
import { useState } from "react";

import { NoteForm } from "~ui/molecules/NoteForm/NoteForm";

const tPinned = "Pinned";
const tAdded = "Added";
const tUpdated = "Updated";
const tEditNote = "Edit note";
const tDeleteNote = "Delete note";
const tCancel = "Cancel";
const tSaveChanges = "Save Changes";
const tDeleteConfirmation = "Are you sure you want to delete this note? This action cannot be undone.";
const tNotePreview = "Note preview:";
const tEditNoteTitle = "Edit Note";
const tDeleteNoteTitle = "Delete Note";

export interface NoteCardProps {
  note: NonNullable<ProspectDto["notes"]>[number];
  noteModal: {
    editNote: NonNullable<ProspectDto["notes"]>[number] | null;
    noteToDelete: NonNullable<ProspectDto["notes"]>[number] | null;
    openEditModal: (note: NonNullable<ProspectDto["notes"]>[number]) => void;
    closeEditModal: () => void;
    openDeleteModal: (note: NonNullable<ProspectDto["notes"]>[number]) => void;
    closeDeleteModal: () => void;
    handleUpdateNote: (values: { body: string; pinned: boolean }) => Promise<void>;
    handleDeleteNote: () => Promise<void>;
    handlePinToggle: (note: NonNullable<ProspectDto["notes"]>[number]) => Promise<void>;
  };
}

export const NoteCard = ({ note, noteModal }: NoteCardProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuAction = (action: () => void) => {
    action();
    handleMenuClose();
  };

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          maxWidth: "100%",
          overflow: "hidden",
          p: 2,
          position: "relative", // For absolute positioning of action button
        }}
      >
        {/* Action Menu - Positioned in top right corner */}
        <Box
          sx={{
            position: "absolute",
            right: "8px",
            top: "8px",
          }}
        >
          <IconButton
            size="small"
            onClick={handleMenuClick}
            aria-label="note actions"
            aria-controls={open ? "note-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
          >
            <MoreVert fontSize="small" />
          </IconButton>
          <Menu
            id="note-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            onClick={(e) => e.stopPropagation()}
            anchorOrigin={{
              horizontal: "right",
              vertical: "bottom",
            }}
            transformOrigin={{
              horizontal: "right",
              vertical: "top",
            }}
          >
            <MenuItem onClick={() => handleMenuAction(() => noteModal.handlePinToggle(note))}>
              <PushPin fontSize="small" sx={{ color: note.pinned ? "primary.main" : "inherit", mr: 1 }} />
              {note.pinned ? "Unpin Note" : "Pin Note"}
            </MenuItem>
            <MenuItem onClick={() => handleMenuAction(() => noteModal.openEditModal(note))}>
              <Edit fontSize="small" sx={{ color: "primary.main", mr: 1 }} />
              {tEditNote}
            </MenuItem>
            <MenuItem onClick={() => handleMenuAction(() => noteModal.openDeleteModal(note))}>
              <Delete fontSize="small" sx={{ color: "error.main", mr: 1 }} />
              {tDeleteNote}
            </MenuItem>
          </Menu>
        </Box>

        <Box display="grid" gap={1} sx={{ overflow: "hidden", pr: 6, width: "100%" }}>
          {" "}
          {/* Add right padding for menu */}
          {/* Note Content */}
          <Box sx={{ overflow: "hidden", width: "100%" }}>
            {note.pinned && (
              <Chip icon={<PushPin fontSize="small" />} label={tPinned} size="small" color="primary" sx={{ mb: 1 }} />
            )}
            <Typography sx={{ width: "100%", wordWrap: "break-word" }}>
              <Linkify
                options={{
                  rel: "noopener noreferrer",
                  style: {
                    display: "inline-block",
                    maxWidth: "100%",
                    overflowWrap: "break-word",
                    wordBreak: "break-word",
                  },
                  target: "_blank",
                }}
              >
                {note.body}
              </Linkify>
            </Typography>
          </Box>
          {/* Timestamp */}
          <Typography variant="caption" color="text.secondary">
            {tAdded} {DateTime.fromISO(note.createdAt ?? "").toRelative()}
            {note.updatedAt !== note.createdAt &&
              ` • ${tUpdated} ${DateTime.fromISO(note.updatedAt ?? "").toRelative()}`}
          </Typography>
        </Box>
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={noteModal.editNote?.id === note.id} onClose={noteModal.closeEditModal} maxWidth="sm" fullWidth>
        <DialogTitle>{tEditNoteTitle}</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <NoteForm
              initialValues={{
                body: note.body ?? "",
                pinned: note.pinned ?? false,
              }}
              onSubmit={noteModal.handleUpdateNote}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={noteModal.closeEditModal}>{tCancel}</Button>
          <Button type="submit" form="note-form" variant="contained">
            {tSaveChanges}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={noteModal.noteToDelete?.id === note.id}
        onClose={noteModal.closeDeleteModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{tDeleteNoteTitle}</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <Typography>{tDeleteConfirmation}</Typography>
            <Box mt={2} sx={{ overflow: "hidden", width: "100%" }}>
              <Typography variant="body2" color="text.secondary">
                {tNotePreview}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  maxHeight: "100px",
                  mt: 1,
                  overflow: "auto",
                  p: 2,
                  width: "100%",
                  wordWrap: "break-word",
                }}
              >
                <Linkify
                  options={{
                    rel: "noopener noreferrer",
                    style: {
                      display: "inline-block",
                      maxWidth: "100%",
                      overflowWrap: "break-word",
                      wordBreak: "break-word",
                    },
                    target: "_blank",
                  }}
                >
                  {note.body}
                </Linkify>
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={noteModal.closeDeleteModal}>{tCancel}</Button>
          <Button color="error" onClick={noteModal.handleDeleteNote}>
            {tDeleteNote}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
